import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  closeCurrentDialog,
} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {isNotNil, not} from 'ramda';
import {isArray, isFalse} from 'ramda-adjunct';

import {useGetCurrenciesQuery} from '@dms/api/core';
import {
  BulkEditInvoiceDeliveryNoteRequest,
  BulkPatchDeliveryNoteRequest,
  useGetWarehousesQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getOptionsFromCurrencies, getOptionsFromWarehouses} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  DeliveryNoteListBulkEditForm,
  DeliveryNoteListBulkEditFormSchema,
} from '../types/DeliveryNoteListBulkEditForm';

export type EditMode = 'full' | 'invoice';

interface DeliveryNoteListBulkEditProps extends RequiredTestIdProps {
  rowId: string | string[];
  onSubmit: (
    requestBody: BulkPatchDeliveryNoteRequest | BulkEditInvoiceDeliveryNoteRequest
  ) => Promise<void>;
  editMode: EditMode;
}

export function DeliveryNoteListBulkEdit(props: DeliveryNoteListBulkEditProps) {
  const {data: warehouses, isLoading: areWarehousesLoading} = useGetWarehousesQuery();
  const {data: currencies, isLoading: areCurrenciesLoading} = useGetCurrenciesQuery();

  const handleDiscard = () => closeCurrentDialog();

  const handleSubmit: FormSubmitHandler<DeliveryNoteListBulkEditForm> = (formValues) => {
    if (props.editMode === 'invoice') {
      return props.onSubmit({
        deliveryNoteIds: isArray(props.rowId) ? props.rowId : [props.rowId],
        invoiceNumber: formValues.invoiceNumber.invoiceNumber,
        invoiceIssueDate: formValues.invoiceIssueDate.invoiceIssueDate?.toISOString(),
      });
    }

    return props.onSubmit({
      deliveryNoteId: isArray(props.rowId) ? props.rowId : [props.rowId],
      warehouseId: formValues.warehouse.warehouseId,
      currency: formValues.currency.currency,
      invoiceNumber: formValues.invoiceNumber.invoiceNumber,
      invoiceIssueDate: formValues.invoiceIssueDate.invoiceIssueDate?.toISOString(),
    });
  };

  const defaultValues: DeliveryNoteListBulkEditForm = {
    warehouse: {
      isWarehouseEnabled: false,
    },
    currency: {
      isCurrencyEnabled: false,
    },
    invoiceNumber: {
      isInvoiceNumberEnabled: false,
    },
    invoiceIssueDate: {
      isInvoiceIssueDateEnabled: false,
    },
  };

  return (
    <Form<DeliveryNoteListBulkEditForm>
      experimentalZodSchema={DeliveryNoteListBulkEditFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => {
        const isWarehouseEnabled = formApi.watch('warehouse.isWarehouseEnabled');
        const isCurrencyEnabled = formApi.watch('currency.isCurrencyEnabled');
        const isInvoiceNumberEnabled = formApi.watch('invoiceNumber.isInvoiceNumberEnabled');
        const isInvoiceIssueDateEnabled = formApi.watch(
          'invoiceIssueDate.isInvoiceIssueDateEnabled'
        );
        const warehouseId = formApi.watch('warehouse.warehouseId');
        const currency = formApi.watch('currency.currency');
        const invoiceNumber = formApi.watch('invoiceNumber.invoiceNumber');
        const isInvoiceIssueDate = formApi.watch('invoiceIssueDate.invoiceIssueDate');
        const hasChanges =
          isNotNil(warehouseId) ||
          isNotNil(currency) ||
          isNotNil(invoiceNumber) ||
          isNotNil(isInvoiceIssueDate);

        return (
          <VStack spacing={4}>
            <Show when={props.editMode === 'full'}>
              <HStack spacing={4} align="flex-end">
                <FormField
                  control={control}
                  type="switch"
                  name="warehouse.isWarehouseEnabled"
                  onChange={(value) =>
                    isFalse(value) && formApi.setValue('warehouse.warehouseId', null)
                  }
                  data-testid={suffixTestId('inputs.isWarehouseEnabled', props)}
                />
                <Box flex={2}>
                  <FormField
                    isDisabled={not(isWarehouseEnabled)}
                    isLoading={areWarehousesLoading}
                    control={control}
                    type="choice"
                    name="warehouse.warehouseId"
                    label={i18n.t('entity.warehouse.labels.warehouse')}
                    options={getOptionsFromWarehouses(warehouses)}
                    menuInPortal
                    data-testid={suffixTestId('inputs.warehouseId', props)}
                  />
                </Box>
              </HStack>
            </Show>

            <Show when={props.editMode === 'full'}>
              <HStack spacing={4} align="flex-end">
                <FormField
                  control={control}
                  type="switch"
                  name="currency.isCurrencyEnabled"
                  onChange={(value) =>
                    isFalse(value) && formApi.setValue('currency.currency', null)
                  }
                  data-testid={suffixTestId('inputs.isCurrencyEnabled', props)}
                />
                <Box flex={2}>
                  <FormField
                    isDisabled={not(isCurrencyEnabled)}
                    isLoading={areCurrenciesLoading}
                    control={control}
                    type="choice"
                    name="currency.currency"
                    label={i18n.t('entity.warehouse.labels.currency')}
                    options={getOptionsFromCurrencies(currencies)}
                    menuInPortal
                    data-testid={suffixTestId('inputs.currency', props)}
                  />
                </Box>
              </HStack>
            </Show>

            <HStack spacing={4} align="flex-end">
              <FormField
                control={control}
                type="switch"
                name="invoiceNumber.isInvoiceNumberEnabled"
                onChange={(value) =>
                  isFalse(value) && formApi.setValue('invoiceNumber.invoiceNumber', null)
                }
                data-testid={suffixTestId('inputs.isInvoiceNumberEnabled', props)}
              />
              <Box flex={2}>
                <FormField
                  isDisabled={not(isInvoiceNumberEnabled)}
                  control={control}
                  type="text"
                  name="invoiceNumber.invoiceNumber"
                  label={i18n.t('entity.warehouse.labels.invoiceNumber')}
                  data-testid={suffixTestId('inputs.invoiceNumber', props)}
                />
              </Box>
            </HStack>

            <HStack spacing={4} align="flex-end">
              <FormField
                control={control}
                type="switch"
                name="invoiceIssueDate.isInvoiceIssueDateEnabled"
                onChange={(value) =>
                  isFalse(value) && formApi.setValue('invoiceIssueDate.invoiceIssueDate', null)
                }
                data-testid={suffixTestId('inputs.isInvoiceIssueDateEnabled', props)}
              />
              <Box flex={2}>
                <FormField
                  isDisabled={not(isInvoiceIssueDateEnabled)}
                  control={control}
                  type="date"
                  name="invoiceIssueDate.invoiceIssueDate"
                  label={i18n.t('entity.warehouse.labels.invoiceIssueDate')}
                  data-testid={suffixTestId('inputs.invoiceIssueDate', props)}
                />
              </Box>
            </HStack>

            <ButtonGroup align="right">
              <Button
                variant="secondary"
                title={i18n.t('general.actions.discard')}
                onClick={handleDiscard}
                data-testid={suffixTestId('actions.discard', props)}
              />
              <FormButton
                type="submit"
                control={control}
                title={i18n.t('general.actions.apply')}
                isDisabled={not(hasChanges)}
                data-testid={suffixTestId('actions.submit', props)}
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}
